import { useCallback, useMemo } from "react";

import { useField, useFormikContext, getIn, FormikErrors } from "formik";

import { ValidationBadge } from "components/form";
import { Select } from "components/form/elements";
import {
  RadioGroupField,
  SelectField,
  TextField,
  TiVoTargetsField,
} from "components/form/fields";
import { Column, Row, Section, VerticalTabs } from "components/layout";
import { TIVO_LAUNCH_TYPES, TiVoTargets } from "hooks/tivoApps";

import { PublicationsWarning } from "../../AppDetails/components";
import { LaunchAttributes } from "../components";
import { TEXTS } from "../constants";
import { FIELDS_LAUNCH_CONFIG_TAB, VARIANT_FIELDS } from "../fields";
import { LaunchConfigTab, VariantForm } from "../types";
import { getLaunchAttributes, getLaunchConfigTabField } from "../utils";

type LaunchAttributesTabsError = { launchType?: string; launchMode?: string };

const TAB_LAUNCH_MODE_VALUES = [
  { text: "Intent", value: "intent" },
  { text: "URI", value: "uri" },
];

function isTabValid(tab: LaunchConfigTab, errors: FormikErrors<VariantForm>) {
  const { launchMode, launchType } = tab;
  const { name } = getLaunchConfigTabField(launchType);

  if (!launchMode || !launchType) {
    return false;
  }
  if (!name) {
    return true;
  }

  return Object.values(FIELDS_LAUNCH_CONFIG_TAB)
    .filter((field) => field.modes.includes(launchMode))
    .every((field) => getIn(errors, `${name}.${field.name}`) === undefined);
}

export function TiVoAppFormLaunchConfigTab() {
  const [, { value: config }, { setValue: setConfig }] =
    useField<VariantForm["launchAttributes"]>("launchAttributes");
  const [, { value: tabs, error: tabsError }, { setValue: setTabs }] = useField<
    LaunchConfigTab[]
  >("launchAttributesTabs");
  const [, { value: targets }] = useField<TiVoTargets>(
    VARIANT_FIELDS.LAUNCH_CONFIG.TARGETS
  );
  const { errors, touched } = useFormikContext<VariantForm>();

  const tabLaunchTypeValues = useMemo(
    () =>
      TIVO_LAUNCH_TYPES.map((launchType) => ({
        disabled: tabs.some((tab) => tab.launchType === launchType),
        text: getLaunchConfigTabField(launchType).label,
        value: launchType,
      })),
    [tabs]
  );

  const tabNames = useMemo(
    () =>
      tabs.map((tab) => ({
        invalid: !isTabValid(tab, errors),
        name: getLaunchConfigTabField(tab.launchType).label,
      })),
    [errors, tabs]
  );

  const handleTabAdd = useCallback(() => {
    setTabs([
      ...tabs,
      {
        launchMode: "",
        launchType: "",
      },
    ]);
  }, [tabs, setTabs]);

  const handleLaunchTypeChange = useCallback(
    (index: number, type: LaunchConfigTab["launchType"]) => {
      const currentTab = tabs[index];
      // no change if type is the same or is missing
      if (!type || type === currentTab.launchType) {
        return;
      }
      const newConfig = { ...config };
      if (!currentTab.launchType) {
        // initial launch type change -> we need to create default data
        newConfig[type] = {
          ...getLaunchAttributes(),
          validationMode: "uri", // value of validation does not really matter at this point, but we need to set it to satisfy types
        };
      } else {
        // next launch type change -> we need to move data from previous to new launch type
        newConfig[type] = {
          ...newConfig[currentTab.launchType],
          validationMode: currentTab.launchMode || "uri",
        };
        delete newConfig[currentTab.launchType];
        if (currentTab.launchMode === "intent") {
          newConfig[type].appendUrlParameters = newConfig[
            type
          ].appendUrlParameters?.filter(({ key, name }) => key && name);
        }
      }
      setConfig(newConfig);
      const newTabs = [...tabs];
      newTabs[index].launchType = type;
      setTabs(newTabs);
    },
    [config, tabs, setConfig, setTabs]
  );

  const handleLaunchModeChange = useCallback(
    (index: number, mode: LaunchConfigTab["launchMode"]) => {
      const launchType = tabs[index].launchType;
      const newConfig = { ...config };
      if (launchType && newConfig[launchType] && mode) {
        const newTabs = [...tabs];
        newTabs[index].launchMode = mode;
        setTabs(newTabs);

        newConfig[launchType].validationMode = mode;
        setConfig(newConfig);
      }
    },
    [tabs, setTabs, config, setConfig]
  );

  const handleTabRemove = useCallback(
    (index: number) => {
      const nextTabs = [...tabs];
      const [tab] = nextTabs.splice(index, 1);
      setTabs(nextTabs);
      if (tab.launchType) {
        const nextConfig = { ...config };
        delete nextConfig[tab.launchType];
        setConfig(nextConfig);
      }
    },
    [config, tabs, setTabs, setConfig]
  );
  return (
    <>
      <Row>
        <Column>
          <Section header={TEXTS.LAUNCH_CONFIG.BASIC_HEADER}>
            <TextField {...VARIANT_FIELDS.LAUNCH_CONFIG.VARIANT_LABEL} />
            <SelectField {...VARIANT_FIELDS.LAUNCH_CONFIG.APP_TYPE} />
            <RadioGroupField
              {...VARIANT_FIELDS.LAUNCH_CONFIG.ADS_ENABLED}
              inline={true}
            />
          </Section>
        </Column>
        <Column>
          <Section header={TEXTS.LAUNCH_CONFIG.TARGET_HEADER}>
            <TiVoTargetsField {...VARIANT_FIELDS.LAUNCH_CONFIG.TARGETS} />
            <PublicationsWarning targets={targets} />
          </Section>
        </Column>
      </Row>
      <Section header={TEXTS.LAUNCH_CONFIG.ATTRIBUTES_HEADER}>
        <VerticalTabs
          isAddButtonDisplayed={tabs.length < TIVO_LAUNCH_TYPES.length}
          tabNames={tabNames}
          dataTestId="launch-attributes-tabs"
          onAdd={handleTabAdd}
          onRemove={handleTabRemove}
        >
          {(tabIndex: number) => {
            const tab = tabs[tabIndex];
            const tabError = (
              tabsError as unknown as LaunchAttributesTabsError[]
            )?.[tabIndex]; // This type convertion is necessary as our current usage of Formik Field is not aligned with Formiks Typing
            const tabTouched = touched?.launchAttributesTabs?.[tabIndex];

            if (!tab) {
              return null;
            }
            const { name } = getLaunchConfigTabField(tab.launchType);
            return (
              <div>
                <Row>
                  <Column>
                    <Select
                      name="tabLaunchType"
                      label={TEXTS.LAUNCH_CONFIG.TAB_LAUNCH_TYPE_LABEL}
                      value={tab.launchType}
                      values={tabLaunchTypeValues}
                      initOption={{ text: "", value: "" }}
                      error={tabError?.launchType}
                      touched={tabTouched?.launchType}
                      dataTestId="launch-attributes-tab-launch-type"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleLaunchTypeChange(
                          tabIndex,
                          e.target.value as LaunchConfigTab["launchType"]
                        )
                      }
                    />
                  </Column>
                  <Column>
                    <Select
                      name="tabLaunchMode"
                      label={TEXTS.LAUNCH_CONFIG.TAB_LAUNCH_MODE_LABEL}
                      value={tab.launchMode}
                      values={TAB_LAUNCH_MODE_VALUES}
                      initOption={{ value: "", text: "" }}
                      error={tabError?.launchMode}
                      touched={tabTouched?.launchMode}
                      dataTestId="launch-attributes-tab-launch-mode"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleLaunchModeChange(
                          tabIndex,
                          e.target.value as LaunchConfigTab["launchMode"]
                        )
                      }
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    {tab.launchMode && name && (
                      <LaunchAttributes mode={tab.launchMode} name={name} />
                    )}
                  </Column>
                </Row>
              </div>
            );
          }}
        </VerticalTabs>
        <ValidationBadge
          error={
            typeof (errors.launchAttributes as unknown) === "string"
              ? (errors.launchAttributes as unknown as string)
              : undefined
          }
          touched={touched}
          dataTestId="launch-attributes-error"
        />
      </Section>
    </>
  );
}
